import { savePushSubscription } from './api';

const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY?.trim();

function urlBase64ToUint8Array(value: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let index = 0; index < raw.length; index += 1) output[index] = raw.charCodeAt(index);
  return output;
}

export function canUsePush(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

export function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches ||
    ('standalone' in navigator && Boolean((navigator as Navigator & { standalone?: boolean }).standalone));
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  const hasController = Boolean(navigator.serviceWorker.controller);
  const reloadKey = 'locus-sw-update-reloaded-v1';
  const didReloadForUpdate = sessionStorage.getItem(reloadKey) === 'true';
  if (hasController && !didReloadForUpdate) {
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      sessionStorage.setItem(reloadKey, 'true');
      window.location.reload();
    }, { once: true });
  }
  const registration = await navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' });
  await registration.update();
  if (didReloadForUpdate) sessionStorage.removeItem(reloadKey);
  return registration;
}

export async function enablePushNotifications(profileId: string): Promise<void> {
  if (!canUsePush()) throw new Error('Questo browser non supporta le notifiche Push.');
  if (!vapidPublicKey) throw new Error('Le notifiche Push non sono ancora configurate.');
  if (isIos() && !isStandalone()) {
    throw new Error('Su iPhone aggiungi prima L.O.C.U.S. alla schermata Home.');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Permesso notifiche non concesso.');

  const registration = await registerServiceWorker();
  if (!registration) throw new Error('Service worker non disponibile.');

  const existingSubscription = await registration.pushManager.getSubscription();
  const subscription = existingSubscription || await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
  });
  await savePushSubscription(profileId, subscription);
}

export async function hasActivePushSubscription(): Promise<boolean> {
  if (!canUsePush() || Notification.permission !== 'granted') return false;
  const registration = await navigator.serviceWorker.getRegistration();
  return Boolean(await registration?.pushManager.getSubscription());
}
